import { useParams, useSearchParams } from "react-router-dom";
import { Printer } from "lucide-react";
import { useFetch, inr, lastMonth } from "../lib/api";
import { useBrandImage } from "../lib/brand";
import { useMember } from "../lib/member";

const td = "border border-gray-400 px-1.5 py-1 align-top";
const th = `${td} bg-emerald-800 text-white text-left font-semibold`;

const T = ({ head, rows, testid }) => (
  <table className="w-full border-collapse text-[10px] mb-1" data-testid={testid}>
    <thead><tr>{head.map((h) => <th key={h} className={th}>{h}</th>)}</tr></thead>
    <tbody>{rows.map((r, i) => <tr key={i} className="break-inside-avoid">{r.map((c, j) => <td key={j} className={td}>{c ?? "—"}</td>)}</tr>)}</tbody>
  </table>);

const H = ({ children }) => <h2 className="text-emerald-800 font-bold text-sm mt-5 mb-1.5">{children}</h2>;

export default function ContractorPrint() {
  const { vid } = useParams();
  const [q] = useSearchParams();
  const month = q.get("month") || lastMonth();
  const withWorkers = q.get("workers") !== "0";
  const { data: d, error } = useFetch(`/pe/contractor/${vid}/profile?month=${month}`);
  const { me } = useMember();
  const co = me?.company;
  const logo = useBrandImage("logo");
  if (error) return <p className="p-8 text-sm" data-testid="cp-print-error">Contractor not found or not visible to you.</p>;
  if (!d) return <p className="p-8 text-sm">Loading…</p>;
  const { vendor: v, workers: w, wages: wg, filings: f, score: s } = d;
  return (
    <div className="bg-white text-black min-h-screen" data-testid="contractor-print-page">
      <div className="no-print sticky top-0 bg-gray-100 border-b p-3 flex items-center gap-3 text-sm">
        <button onClick={() => window.print()} className="inline-flex items-center gap-1.5 bg-emerald-700 text-white px-3 py-1.5 rounded" data-testid="cp-print-now"><Printer size={14} />Print / Save as PDF</button>
        <span className="text-gray-600">Tip: choose "Save as PDF" as the printer to keep a copy for audits.</span>
      </div>
      <div className="max-w-[900px] mx-auto p-8 print:p-0 text-[11px]">
        <div className="flex items-center justify-between border-b-2 border-gray-800 pb-2 mb-3" data-testid="cp-letterhead">
          {logo ? <img src={logo} alt="logo" className="max-h-14 max-w-[220px] object-contain" /> : <div className="font-bold text-sm">{co?.name}</div>}
          <div className="text-right text-[10px] text-gray-700"><div className="font-bold text-sm">{co?.name}</div><div>{co?.address}</div></div>
        </div>
        <h1 className="text-lg font-bold">Contractor profile — {v.vendor_name}</h1>
        <div className="text-gray-600">Principal employer: {d.employer?.company_name || "—"} · Month: {month} · Generated {new Date().toISOString().slice(0, 10)}</div>
        <H>Registration details</H>
        <T head={["PAN", "EPF code", "ESIC code", "CLRA licence", "CLRA valid till", "Max workers", "Contact email"]}
          rows={[[v.pan_number, v.epf_registration_number, v.esic_registration_number, v.clra_license_number, v.clra_license_expiry, v.clra_max_workers, v.contact_email]]} />
        <H>Summary for {month}</H>
        <T head={["Active", "Total", "Paid", "No UAN", "No ESIC IP", "Gross wages", "PF due", "ESIC due", "Below min. wage", "Overdue filings"]}
          rows={[[w.active, w.total, w.paid_this_month, w.no_uan, w.no_esic, inr(wg.gross), inr(wg.pf), inr(wg.esic), wg.breaches, f.overdue]]} testid="cp-print-summary" />
        {s?.score != null && <>
          <H>Compliance score: {s.score}/100 · Grade {s.grade}{s.in_progress ? " (in progress)" : ""} · 6-month average {s.average_6m ?? "—"}</H>
          <T head={["Part", "Points", "Note"]} rows={s.breakdown.map((b) => [b.label, `${b.points}/${b.max}`, b.note])} /></>}
        <H>Payment holds & open alerts</H>
        {d.holds.length || d.alerts.length ? <T head={["Month", "Status", "Code", "Reason"]}
          rows={[...d.holds.map((h) => [h.billing_month, h.released ? "Released" : "On hold", h.reason_code, h.reason]), ...d.alerts.map((a) => [a.month, "Open alert", a.code, a.title])]} /> : <p>None.</p>}
        <H>Statutory filings (PF & ESIC, last 12 months)</H>
        {f.rows.length ? <T head={["Month", "Type", "Workers", "Due", "Paid", "Due date", "Status", "Filed on", "Challan no."]}
          rows={f.rows.map((r) => [r.month, r.type, r.workers, inr(r.amount_due), inr(r.amount_paid), r.due_date, r.status, r.filed_on, (r.challan_numbers || []).join(", ")])} /> : <p>No PF or ESIC dues yet.</p>}
        <H>Challan reminders sent</H>
        {d.reminders.length ? <T head={["Sent at", "Month", "Reminder", "Missing", "Emailed to"]}
          rows={d.reminders.map((r) => [new Date(r.at).toLocaleString("en-IN"), r.month, r.stage === "final" ? "Final" : "First", r.missing.join(", "), (r.to || []).join(", ") || "compliance inbox only"])} /> : <p>No reminders sent.</p>}
        {withWorkers ? <><H>Workers ({w.total})</H>
          <T head={["Worker", "UAN", "ESIC IP", "Skill", "Site", "Department", "Status", "Paid"]} testid="cp-print-workers"
            rows={w.list.map((x) => [x.name, x.uan, x.esic, x.skill, x.site, x.department, x.active ? "Active" : "Inactive", x.paid ? "Yes" : "—"])} /></>
          : <H>Workers: {w.active} active of {w.total} (full list omitted)</H>}
        <p className="mt-8">Reviewed by: ____________________________ &nbsp;&nbsp; Date: ______________</p>
      </div>
    </div>);
}
